import propData from '@/data/props.json';
import {
  SITE_MODEL_FITS,
  SITE_MODEL_STYLES,
  type SiteModelFit,
  type SiteModelStyle,
} from './SiteObjectModelSource';

/**
 * The shipped prop catalog — what authored meshes exist, how big they are, and where they came from.
 *
 * Generated by `scripts/props/fetch-props.mjs` from `scripts/props/sources.json`; not hand-edited.
 * That split matters. The manifest holds intent and licence — the things a person decides — while
 * this file holds the numbers the build actually measured off the geometry that shipped. Sizes are
 * never guessed, and never drift from the mesh they describe, because nobody types them.
 *
 * ## Licence is a required field, deliberately
 *
 * These are third-party assets redistributed inside the application, so every record names its
 * licence, author, and source. {@link assertValidPropDefinition} rejects a record missing any of the
 * three, and the importer refuses to write one. Attribution that relies on someone remembering is
 * attribution that does not survive the second year of a project — `src/assets/props/NOTICES.md` is
 * generated from these same fields so the credit surface cannot fall out of step with the library.
 */

/**
 * What kind of object this is — the sections an object palette shows, and the only taxonomy the
 * catalog commits to.
 *
 * The first seven were what the reference pipeline happened to need. The rest were added when props
 * became the third placeable class alongside flora and fauna, because a palette a designer browses
 * has to cover the things a garden actually contains: a birdbath is not a `feature` in any sense that
 * helps someone find it, and filing a swing set under `structure` next to a shed is how a category
 * list stops being useful.
 *
 * Appended rather than reordered, deliberately. Existing catalog records keep their categories and
 * `categories()` keeps returning them in the order it always did; a palette that wants a different
 * running order can sort, and that is a display decision rather than a data one.
 */
export type PropCategory =
  | 'seating'
  | 'lighting'
  | 'container'
  | 'feature'
  | 'rock'
  | 'utility'
  | 'structure'
  | 'water'
  | 'play'
  | 'art'
  | 'edging'
  | 'vehicle';

export const PROP_CATEGORIES: readonly PropCategory[] = [
  'seating',
  'lighting',
  'container',
  'feature',
  'rock',
  'utility',
  'structure',
  'water',
  'play',
  'art',
  'edging',
  'vehicle',
];

const CATEGORY_LABELS: Record<PropCategory, string> = {
  seating: 'Seating',
  lighting: 'Lighting',
  container: 'Planters & containers',
  feature: 'Features',
  rock: 'Rock & stone',
  utility: 'Utility',
  structure: 'Structures',
  water: 'Water',
  play: 'Play',
  art: 'Art & ornament',
  edging: 'Edging & borders',
  vehicle: 'Vehicles',
};

export function propCategoryLabel(category: PropCategory): string {
  return CATEGORY_LABELS[category];
}

export interface PropLicense {
  /** SPDX identifier, e.g. `CC0-1.0`. */
  spdx: string;
  author: string;
  sourceUrl: string;
}

export interface PropDefinition {
  id: string;
  name: string;
  category: PropCategory;
  tags: string[];
  /** File name within `src/assets/props`. */
  file: string;
  /** The asset's own measured size in meters — the default dimensions a placed prop takes. */
  size: { widthMeters: number; heightMeters: number; depthMeters: number };
  /** Triangle count after reduction. Reported to the inspector so scene cost stays visible. */
  triangles: number;
  fit: SiteModelFit;
  style: SiteModelStyle;
  /** Correction for assets that do not face +Z, in degrees. */
  yawOffsetDegrees: number;
  sinkMeters: number;
  clearanceMeters: number;
  colors: { body: string; trim: string };
  license: PropLicense;
}

interface PropCatalogFile {
  catalogVersion: string;
  schemaVersion: number;
  props: unknown[];
}

export class PropCatalog {
  private readonly byId = new Map<string, PropDefinition>();
  readonly version: string;

  constructor(file: PropCatalogFile) {
    this.version = file.catalogVersion;
    for (const record of file.props) {
      assertValidPropDefinition(record);
      if (this.byId.has(record.id)) throw new Error(`Duplicate prop id in catalog: ${record.id}`);
      this.byId.set(record.id, record);
    }
  }

  get size(): number {
    return this.byId.size;
  }

  list(): PropDefinition[] {
    return [...this.byId.values()];
  }

  get(id: string): PropDefinition | undefined {
    return this.byId.get(id);
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  byCategory(category: PropCategory): PropDefinition[] {
    return this.list().filter((prop) => prop.category === category);
  }

  /** Categories actually present, in catalog order — what a picker should show as sections. */
  categories(): PropCategory[] {
    return PROP_CATEGORIES.filter((category) => this.byCategory(category).length > 0);
  }

  /** Every tag in use, sorted, for a facet list. */
  tags(): string[] {
    const tags = new Set<string>();
    for (const prop of this.list()) for (const tag of prop.tags) tags.add(tag);
    return [...tags].sort();
  }

  /**
   * Free-text search over name, tags, and category.
   *
   * Mirrors `FaunaCatalog.search` so the two asset-backed catalogs answer "what can I place" the same
   * way, and so an object palette does not have to re-derive matching rules the fauna palette already
   * settled. Category is searched because "lighting" is a word people type expecting the lamps.
   */
  search(query: string): PropDefinition[] {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) return this.list();
    return this.list().filter((prop) =>
      [prop.name, prop.category, ...prop.tags].join(' ').toLowerCase().includes(needle),
    );
  }

  /**
   * Attribution lines for every prop, deduplicated by author and licence.
   *
   * Grouped rather than listed per asset because a credits panel that repeats "Poly Haven, CC0-1.0"
   * ten times is one nobody reads, and unread credit is not credit.
   */
  attributions(): { spdx: string; author: string; props: PropDefinition[] }[] {
    const groups = new Map<string, { spdx: string; author: string; props: PropDefinition[] }>();
    for (const prop of this.list()) {
      const key = `${prop.license.author} ${prop.license.spdx}`;
      const group = groups.get(key) ?? {
        spdx: prop.license.spdx,
        author: prop.license.author,
        props: [],
      };
      group.props.push(prop);
      groups.set(key, group);
    }
    return [...groups.values()];
  }
}

/**
 * Validate a catalog record, throwing with a field-specific message.
 *
 * Invalid records throw rather than being dropped, matching `assertValidFloraDefinition`: a catalog
 * that silently ships nine of the ten props it claims is worse than one that refuses to start, since
 * the failure then shows up as a missing bench in someone's design rather than as a build error.
 */
export function assertValidPropDefinition(raw: unknown): asserts raw is PropDefinition {
  const where = (field: string): string => `Prop catalog record ${id(raw) ?? '<no id>'}: ${field}`;
  if (!raw || typeof raw !== 'object') throw new Error('Prop catalog record is not an object.');
  const record = raw as Record<string, unknown>;

  for (const field of ['id', 'name', 'file'] as const) {
    if (typeof record[field] !== 'string' || (record[field]).length === 0) {
      throw new Error(where(`${field} must be a non-empty string.`));
    }
  }

  if (!PROP_CATEGORIES.includes(record.category as PropCategory)) {
    throw new Error(where(`category "${describe(record.category)}" is not a known category.`));
  }

  if (!Array.isArray(record.tags) || record.tags.some((tag) => typeof tag !== 'string')) {
    throw new Error(where('tags must be an array of strings.'));
  }

  // Checked because both fields silently change how the mesh joins the scene rather than failing:
  // an unknown `style` would fall through the styling switch and ship the asset's raw photoreal
  // materials into a flat-shaded garden, which reads as a rendering bug rather than a bad record.
  if (!SITE_MODEL_FITS.includes(record.fit as SiteModelFit)) {
    throw new Error(
      where(`fit "${describe(record.fit)}" must be one of: ${SITE_MODEL_FITS.join(', ')}.`),
    );
  }
  if (!SITE_MODEL_STYLES.includes(record.style as SiteModelStyle)) {
    throw new Error(
      where(`style "${describe(record.style)}" must be one of: ${SITE_MODEL_STYLES.join(', ')}.`),
    );
  }

  const size = record.size as Record<string, unknown> | undefined;
  for (const axis of ['widthMeters', 'heightMeters', 'depthMeters'] as const) {
    const value = size?.[axis];
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
      throw new Error(where(`size.${axis} must be a positive number.`));
    }
  }

  const license = record.license as Record<string, unknown> | undefined;
  for (const field of ['spdx', 'author', 'sourceUrl'] as const) {
    if (typeof license?.[field] !== 'string' || (license[field]).length === 0) {
      throw new Error(
        where(`license.${field} is required — third-party assets must carry attribution.`),
      );
    }
  }

  const colors = record.colors as Record<string, unknown> | undefined;
  for (const slot of ['body', 'trim'] as const) {
    if (typeof colors?.[slot] !== 'string' || !/^#[0-9a-f]{6}$/i.test(colors[slot])) {
      throw new Error(where(`colors.${slot} must be a #rrggbb string.`));
    }
  }
}

function id(raw: unknown): string | undefined {
  const value = (raw as Record<string, unknown> | null)?.id;
  return typeof value === 'string' ? value : undefined;
}

/** Render an unknown field value for an error message — a bad field is often not a string. */
function describe(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/** The default catalog loaded from the shipped `props.json`. */
export const propCatalog = new PropCatalog(propData);
